import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Box, Button, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'

import { Icons } from '../../../utils/icons.jsx'

export default function CustomCodeBlock ( { className, children, ...props } ) {


    const [copied, setCopied] = useState( false )
    const match = /language-(\w+)/.exec( className || '' )
    const language = match ? match[1] : ''
    const codeString = String( children ).replace( /\n$/, '' )

    const { palette, layout, border, theme } = useTheme()

    const handleCopy = async () => {
        await navigator.clipboard.writeText( codeString )

        setCopied( true )
        setTimeout( () => setCopied( false ), 2000 )
    }

    if ( !match ) {
        // Render inline code differently so it blends into standard paragraphs
        return (
            <Box
                component="code"
                sx={{
                    backgroundColor: 'action.hover',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 5,
                    fontFamily: 'monospace',
                    fontSize: '0.85em',
                }}
                {...props}
            >
                {children}
            </Box>
        )
    }

    return (
        <Box
            sx={{
                borderRadius: 5,
                overflow: 'hidden',
                my: 2,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            {/* Code Block Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    backgroundColor: palette.contentBg,
                    borderBottom: border[1],
                    "& *": {
                        margin: 0
                    }
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontFamily: 'monospace',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 0.5
                    }}
                >
                    {language}
                </Typography>
                <Button
                    size="small"
                    variant="text"
                    onClick={handleCopy}
                    startIcon={copied ? Icons.default.CheckIcon : Icons.default.Copy}
                    sx={{
                        color: copied ? 'success.main' : 'grey.400',
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        py: 0,
                        '&:hover': { color: 'common.white' },
                    }}
                >
                    {copied ? 'Copied!' : 'Copy'}
                </Button>
            </Box>

            {/* Code Block Content */}
            <Box sx={{
                '& pre': {
                    margin: '0 !important',
                    borderRadius: '0 !important',
                }
            }}>
                <SyntaxHighlighter
                    style={theme === 'dark' ? vscDarkPlus : prism}
                    language={language}
                    PreTag="div"
                    {...props}
                    className="slim-custom-scrollbar"
                    customStyle={{
                        margin: "0",
                        background: theme === 'dark' ? palette.action.selected : "white"
                    }}
                >
                    {codeString}
                </SyntaxHighlighter>
            </Box>
        </Box>
    )
}

export function CustomCodeBlockReadOnly ( { className, children, ...props } ) {


    const [copied, setCopied] = useState( false )
    const match = /language-(\w+)/.exec( className || '' )
    const language = match ? match[1] : ''
    const codeString = String( children ).replace( /\n$/, '' )

    const { palette, border, theme } = useTheme()

    if ( !match ) {
        // Render inline code differently so it blends into standard paragraphs
        return (
            <Box
                component="code"
                sx={{
                    backgroundColor: 'action.hover',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 5,
                    fontFamily: 'monospace',
                    fontSize: '0.85em',
                }}
                {...props}
            >
                {children}
            </Box>
        )
    }

    return (
        <Box
            sx={{
                borderRadius: 5,
                overflow: 'hidden',
                my: 2,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            {/* Code Block Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    backgroundColor: palette.contentBg,
                    borderBottom: border[1],
                    "& *": {
                        margin: 0
                    }
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontFamily: 'monospace',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 0.5
                    }}
                >
                    {language}
                </Typography>
            </Box>

            {/* Code Block Content */}
            <Box sx={{
                '& pre': {
                    margin: '0 !important',
                    borderRadius: '0 !important',
                }
            }}>
                <SyntaxHighlighter
                    style={theme === 'dark' ? vscDarkPlus : prism}
                    language={language}
                    PreTag="div"
                    {...props}
                    className="slim-custom-scrollbar"
                    customStyle={{
                        margin: "0",
                        background: theme === 'dark' ? palette.action.selected : "white",
                        fontSize: "0.75rem"
                    }}
                >
                    {codeString}
                </SyntaxHighlighter>
            </Box>
        </Box>
    )
}